<template>
  <dashboard-widget
    :is-loading="isLoading"
    is-panel>
    <template>
      <div class="latest-activity">
        <div v-if="projects.length > 0 && !isError">
          <h4>{{ $t('ui.dashboard.most_active_projects') }}</h4>
          <div :key="index" v-for="(project, index) in projects" class="project-panel">
            <router-link :to="{ name:'project_leads', params: { id: project.project.id }}" tag="div">
              <div class="project-header">
                <h5 class="truncate-text">{{ project.project.name }}</h5>
              </div>
              <div class="project-body">
                <div class="row gutter-sm">
                  <div class="col-auto">
                    <p class="caption pre">{{$t('ui.dashboard.top_projects.kits')}}</p>
                    <p class="accent truncate-text">{{project.project.business_kits_count}}</p>
                  </div>

                  <div class="col-auto">
                    <p class="caption pre">{{$t('ui.dashboard.top_projects.leads')}}</p>
                    <p class="accent truncate-text">{{project.leads_count}}</p>
                  </div>

                  <div class="col-auto">
                    <p class="caption pre">{{$t('ui.dashboard.top_projects.my_leads')}}</p>
                    <p class="accent truncate-text">{{project.users_leads_count}}</p>
                  </div>

                  <div class="col">
                    <p class="caption pre">{{$t('ui.dashboard.top_projects.lead_sheet')}}</p>
                    <p class="accent truncate-text">{{project.project.lead_mask.name}}</p>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>

        <div v-else-if="projects.length === 0 && !isError" class="aligner">
            <div class="text-center empty-state">
                <img src="../../../../../assets/project-empty.svg">
                <p class="empty-state-margin">{{$t('ui.dashboard.top_projects.empty_state')}}</p>
                <router-link class="link" :to="{ name: 'project_new' }">{{ $t('ui.dashboard.activity_list_widget.empty_state_intro') }}</router-link>
            </div>
        </div>

        <div v-if="isError" class="aligner">
            <div class="text-center empty-state">
                <img src="../../../../../assets/project-empty.svg">
                <p class="empty-state-margin">{{$t('ui.dashboard.widget_error')}}</p>
            </div>
        </div>
      </div>
    </template>
  </dashboard-widget>
</template>

<script>
import map from 'lodash/map'
import isNull from 'lodash/isNull'
import get from 'lodash/get'
import join from 'lodash/join'
import find from 'lodash/find'
import eq from 'lodash/eq'
import size from 'lodash/size'
import flow from 'lodash/flow'
import slice from 'lodash/fp/slice'
import reject from 'lodash/fp/reject'

import Charts from '@/api/Charts.js'
import Api from '@/api/Api.js'
import WidgetMixin from './mixins/WidgetMixin'
import WidgetPeriodMixin from './mixins/WidgetPeriodMixin'
import WidgetQueryMixin from './mixins/WidgetQueryMixin'

const MOST_ACTIVE_PROJECTS_LIMIT = 2

export default {
  mixins: [WidgetMixin, WidgetPeriodMixin, WidgetQueryMixin],

  props: {
    identifier: {
      type: String,
      default: 'most-active-projects'
    }
  },

  data: () => ({
    projects: []
  }),

  computed: {
    userIdentifier: {
      get () {
        return this.$store.state.current_user.id || this.$store.state.current_user.user_id
      }
    }
  },

  methods: {
    async refresh() {
        this.setIsLoading(true)

        try {
            let projects = await this.get(this.from(this.filter.period), this.filter.projectId)
            this.projects = this.transform(projects)

            // After loaded once, the widget should immediately indicate that loading is finished
            // since it's independent from project filter
            this.eventBus.$on('project', async () => this.eventBus.$emit('widget-loaded', this.identifier))
        } catch (error) {
            this.setError(error)
            this.setIsError(true)
        } finally {
            this.setIsLoading(false)
            this.eventBus.$emit('widget-loaded', this.identifier)
        }
    },

    async get (from, project) {
      return new Promise(async (resolve, reject) => {
        let charts = new Charts()

        // Temporary query more events for the case if some are archived
        // or doesn't match the actual leads
        const margin = 15

        let tuples = [
          { key: 'from', value: `"${from}"` },
          { key: 'userId', value: this.userIdentifier },
          { key: 'limit', value: MOST_ACTIVE_PROJECTS_LIMIT + margin }
        ]

        if (!!project) {
          tuples.push({ key: 'projectId', value: project })
        }

        const querySchema = `
          project_id
          leads_count
          users_leads_count
        `
        const query = this.getQuery(tuples, 'mostActiveProjects', querySchema)
        let { most_active_projects: mostActiveProjects } = await charts
          .postQuery({
            organization_id: this.filter.organizationId,
            operationName: null,
            variables: {},
            query: `query Query {
              ${query}
            }`
          })
          .catch(reject)

        // If no projects returned, no need for querying API
        if (eq(size(mostActiveProjects), 0)) {
          resolve(mostActiveProjects)

          return
        }

        let api = new Api()
        const projectIds = join(map(mostActiveProjects, ({ project_id }) => project_id))
        const projects = await api
          .getMultiple('projects', 'id', projectIds)
          .catch(reject)

        mostActiveProjects = map(mostActiveProjects, mostActiveProject => {
          // Get project
          if (!isNull(mostActiveProject.project_id)) {
            mostActiveProject.project = find(projects, ['id', mostActiveProject.project_id])
          }

          return mostActiveProject
        })

        resolve(mostActiveProjects)
      })
    },

    // Transform for easier handling and limit
    transform: mostActiveProjects => flow([
      reject(['project', void 0]),
      slice(0, MOST_ACTIVE_PROJECTS_LIMIT)
    ])(mostActiveProjects)
  }
}
</script>

<style lang="sass" scoped>
h3
  text: black
  padding: 10px

img
  display: inline-block
  padding: 10px

.filter-lists
  display: inline-block
  float: right

.project-panel
  background: $paper-lighter
  border-radius: 5px
  padding: 20px
  margin-top: 10px
  transition: all .15s ease-out
  &:hover
    background: $paper-light
    cursor: pointer
  h5
    margin-top: 0px !important
    margin-bottom: 10px !important
  .project-header
  .project-body
    .pre
      font-weight: $font-medium !important
      color: $ink-lighter !important
      margin-bottom: 8px
    .accent
      color: $ink !important
      margin-bottom: 0px !important
</style>



// WEBPACK FOOTER //
// MostActiveProjectsWidget.vue?174fad1e